import { Card } from '@/ui/Card';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { estadoDelPorcentaje, useBienestar } from './api';
import { AlertasAccionables, FilaDeArea, Indicador, Indicadores, Titulo } from './piezas';

/**
 * Bienestar — el tablero que cruza inducción, clima, reconocimiento, eventos y
 * solicitudes, con sus alertas accionables.
 *
 * Las alertas llevan a la pantalla donde se arregla lo que avisan, y el
 * servidor es quien decide a cuál: manda la clave del `PAGE_MAP` en `page`.
 */
export function Bienestar({ activa }: { activa: boolean }) {
  const consulta = useBienestar(activa);
  const d = consulta.data;

  if (consulta.isPending) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }
  if (consulta.isError) {
    return (
      <EstadoError
        err={consulta.error}
        que="el tablero de Bienestar"
        onReintentar={() => void consulta.refetch()}
      />
    );
  }
  if (!d) return null;

  const r = d.resumen;
  return (
    <div className="flex flex-col gap-6">
      <p className="text-2 text-muted">
        <i className="bi bi-funnel" aria-hidden="true" /> Alcance: {d.scope}
      </p>

      <Indicadores>
        <Indicador icono="bi-person-check-fill" valor={r.usuarios_activos} etiqueta="Colaboradores activos" />
        <Indicador
          icono="bi-mortarboard-fill"
          estado={estadoDelPorcentaje(r.induccion_al_dia_pct)}
          valor={`${r.induccion_al_dia_pct} %`}
          etiqueta="Inducción al día"
          nota={`${r.induccion_pendiente} pendientes`}
        />
        <Indicador
          icono="bi-clipboard-data-fill"
          estado={estadoDelPorcentaje(r.clima_participacion_pct, 60, 30)}
          valor={`${r.clima_participacion_pct} %`}
          etiqueta="Participación en clima"
          nota={d.clima.survey ?? 'sin encuesta activa'}
        />
        <Indicador icono="bi-star-fill" valor={r.kudos_mes} etiqueta="Kudos del mes" />
        <Indicador icono="bi-file-earmark-text-fill" valor={r.solicitudes_pendientes} etiqueta="Solicitudes pendientes" />
        <Indicador icono="bi-calendar-event-fill" valor={r.eventos_proximos} etiqueta="Eventos próximos" />
      </Indicadores>

      <section aria-labelledby="ana-alertas">
        <Titulo icono="bi-bell-fill"><span id="ana-alertas">Alertas accionables</span></Titulo>
        <AlertasAccionables alertas={d.alertas} />
      </section>

      {d.por_area.length > 0 && (
        <section aria-labelledby="ana-por-area">
          <Titulo icono="bi-diagram-2-fill"><span id="ana-por-area">Inducción por área</span></Titulo>
          <Card className="p-4">
            <ul className="flex flex-col gap-3">
              {d.por_area.map((a) => (
                <FilaDeArea
                  key={a.area}
                  nombre={a.area}
                  pct={a.induccion_pct}
                  estado={estadoDelPorcentaje(a.induccion_pct)}
                  derecha={`${a.induccion_pct} %`}
                  nota={`${a.usuarios} ${a.usuarios === 1 ? 'colaborador' : 'colaboradores'}`}
                />
              ))}
            </ul>
          </Card>
        </section>
      )}
    </div>
  );
}
